/**
 * Custom login domain — operator page (FS-676).
 *
 * Surface for the `/api/v1/custom-login-domain` backend. Two regions:
 *
 *   1. Add-domain form — type the FQDN (e.g. `partners.brand.com`) and
 *      submit. Client-side FQDN validation mirrors the server regex so
 *      typos fail before the POST. A clean 409 surfaces a "domain is
 *      already claimed" error so operators can pick a different hostname
 *      or contact support about a reclaim.
 *
 *   2. Domains table — one row per active login domain with:
 *      - verified / unverified badge
 *      - created_at timestamp
 *      - Verify action (flips the stub verified flag, temporary until the
 *        real DNS/SSO verification feature lands)
 *      - Delete action (soft-deletes the row)
 *
 * Brand: emerald → sky headline gradient, Inter, zinc borders, 6/8px
 * rounded corners, light theme, no emojis, no competitor names.
 *
 * The file is intentionally monolithic — breaking sub-components into
 * sibling files would widen the owned-file surface.
 */

import type React from "react";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createDomain,
	customLoginDomainQueryKeys,
	deleteDomain,
	domainDraftSchema,
	EMPTY_DRAFT,
	formatTimestamp,
	listDomains,
	prependDomain,
	removeDomain,
	replaceDomain,
	sortDomains,
	verifiedBadgeClass,
	verifiedLabel,
	verifyDomain,
	type CreateDomainOutcome,
	type CustomLoginDomainView,
	type DomainDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------

export function CustomLoginDomainPage() {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<DomainDraft>({ ...EMPTY_DRAFT });
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [verifyingId, setVerifyingId] = useState<string | null>(null);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	const listQuery = useQuery({
		queryKey: customLoginDomainQueryKeys.list(),
		queryFn: listDomains,
	});

	const createMutation = useMutation({
		mutationFn: createDomain,
		onSuccess: (outcome: CreateDomainOutcome) => {
			switch (outcome.kind) {
				case "ok":
					toast.success(`Registered ${outcome.domain.domain}.`);
					setDraft({ domain: "" });
					setDraftErrors({});
					queryClient.setQueryData<CustomLoginDomainView[]>(
						customLoginDomainQueryKeys.list(),
						(prev) => (prev ? prependDomain(prev, outcome.domain) : [outcome.domain]),
					);
					queryClient.invalidateQueries({
						queryKey: customLoginDomainQueryKeys.list(),
					});
					break;
				case "already_claimed":
					setDraftErrors({
						domain: outcome.by_current_tenant
							? "You already registered this domain."
							: "This domain is already claimed by another tenant.",
					});
					toast.error("Domain already claimed.");
					break;
				case "invalid":
					setDraftErrors({ domain: outcome.message });
					toast.error(outcome.message);
					break;
			}
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register domain.");
		},
	});

	const verifyMutation = useMutation({
		mutationFn: (id: string) => verifyDomain(id),
		onMutate: (id: string) => {
			setVerifyingId(id);
		},
		onSettled: () => {
			setVerifyingId(null);
		},
		onSuccess: (row, id) => {
			if (!row) {
				toast.error("Domain not found.");
				return;
			}
			toast.success(`Verified ${row.domain}.`);
			queryClient.setQueryData<CustomLoginDomainView[]>(
				customLoginDomainQueryKeys.list(),
				(prev) => (prev ? replaceDomain(prev, row) : prev),
			);
			queryClient.invalidateQueries({
				queryKey: customLoginDomainQueryKeys.list(),
			});
			void id;
		},
		onError: (err: Error) => {
			toast.error(err.message || "Verification failed.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteDomain(id),
		onMutate: (id: string) => {
			setDeletingId(id);
		},
		onSettled: () => {
			setDeletingId(null);
		},
		onSuccess: (_void, id) => {
			toast.success("Domain removed.");
			queryClient.setQueryData<CustomLoginDomainView[]>(
				customLoginDomainQueryKeys.list(),
				(prev) => (prev ? removeDomain(prev, id) : prev),
			);
			queryClient.invalidateQueries({
				queryKey: customLoginDomainQueryKeys.list(),
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove domain.");
		},
	});

	const sortedDomains = useMemo(
		() => (listQuery.data ? sortDomains(listQuery.data) : []),
		[listQuery.data],
	);

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = domainDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					Custom login domain
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Host the publisher portal on your own branded hostname (e.g.
					<span className="mx-1 rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs text-zinc-800">
						partners.brand.com
					</span>
					). Your affiliates sign in at your agency's URL instead of the
					generic Trackerity address.
				</p>
			</header>

			<section aria-labelledby="add-domain-heading" className={`${CARD} p-6`}>
				<h2 id="add-domain-heading" className="text-base font-semibold text-zinc-900">
					Register a domain
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:gap-4"
				>
					<div className="flex w-full flex-col gap-1">
						<label htmlFor="cld-domain" className={LABEL}>
							Domain (FQDN)
						</label>
						<input
							id="cld-domain"
							name="domain"
							type="text"
							className={INPUT}
							placeholder="partners.brand.com"
							value={draft.domain}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, domain: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.domain ? (
							<span className={ERROR_TEXT}>{draftErrors.domain}</span>
						) : (
							<span className={HINT}>
								Must be a fully-qualified hostname — no scheme, port, or path.
								Trackerity.com sub-domains are reserved.
							</span>
						)}
					</div>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? "Registering…" : "Register"}
						</button>
					</div>
				</form>
			</section>

			<section aria-labelledby="domains-heading" className={CARD}>
				<div className="flex items-center justify-between px-6 pt-6">
					<h2 id="domains-heading" className="text-base font-semibold text-zinc-900">
						Registered domains
					</h2>
					<span className={HINT}>
						{sortedDomains.length} active
					</span>
				</div>

				{listQuery.isLoading ? (
					<p className="px-6 pb-6 pt-4 text-sm text-zinc-500">Loading…</p>
				) : listQuery.isError ? (
					<p className="px-6 pb-6 pt-4 text-sm text-red-600">
						Failed to load login domains.
					</p>
				) : sortedDomains.length === 0 ? (
					<p className="px-6 pb-6 pt-4 text-sm text-zinc-500">
						No login domains yet. Register one above to brand the partner
						portal URL.
					</p>
				) : (
					<div className="mt-4 overflow-x-auto">
						<table className="w-full text-left text-sm">
							<thead className="border-t border-zinc-200 bg-zinc-50/60 text-xs uppercase tracking-wide text-zinc-500">
								<tr>
									<th className="px-6 py-3 font-medium">Domain</th>
									<th className="px-6 py-3 font-medium">Status</th>
									<th className="px-6 py-3 font-medium">Created</th>
									<th className="px-6 py-3 text-right font-medium">Actions</th>
								</tr>
							</thead>
							<tbody>
								{sortedDomains.map((d) => (
									<tr key={d.id} className="border-t border-zinc-100 align-middle">
										<td className="px-6 py-3 font-mono text-zinc-900">
											{d.domain}
										</td>
										<td className="px-6 py-3">
											<span
												className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${verifiedBadgeClass(d.verified)}`}
											>
												{verifiedLabel(d.verified)}
											</span>
										</td>
										<td className="px-6 py-3 text-zinc-600">
											{formatTimestamp(d.created_at)}
										</td>
										<td className="px-6 py-3">
											<div className="flex justify-end gap-2">
												<button
													type="button"
													className={BTN_SECONDARY}
													disabled={
														d.verified ||
														(verifyingId === d.id && verifyMutation.isPending)
													}
													onClick={() => verifyMutation.mutate(d.id)}
												>
													{verifyingId === d.id && verifyMutation.isPending
														? "Verifying…"
														: d.verified
															? "Verified"
															: "Verify"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													disabled={
														deletingId === d.id && deleteMutation.isPending
													}
													onClick={() => {
														if (
															confirm(
																`Remove ${d.domain}? Partners will no longer be able to sign in at this URL.`,
															)
														) {
															deleteMutation.mutate(d.id);
														}
													}}
												>
													{deletingId === d.id && deleteMutation.isPending
														? "Removing…"
														: "Delete"}
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</div>
	);
}

export default CustomLoginDomainPage;
